import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BirthdayNumberCalculator = () => {
  const [birthday, setBirthday] = useState('');
  const [birthdayNumber, setBirthdayNumber] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    setBirthday(e.target.value);
    setError('');
  };

  const calculateBirthdayNumber = () => {
    if (!birthday.trim()) {
      setError('Please enter your birthday');
      return;
    }

    // Validate date format
    const date = new Date(birthday);
    if (isNaN(date.getTime())) {
      setError('Please enter a valid date (YYYY-MM-DD)');
      return;
    }

    // Get day, month, and year
    const day = date.getDate();
    const month = date.getMonth() + 1; // Months are 0-based
    const year = date.getFullYear();

    // Calculate birthday number
    const birthdaySum = day + month + year;
    const reduceNumber = (num) => {
      while (num > 9 && ![11, 22, 33].includes(num)) {
        num = num.toString().split('').reduce((sum, digit) => sum + +digit, 0);
      }
      return num;
    };

    const finalNumber = reduceNumber(birthdaySum);
    setBirthdayNumber(finalNumber);

    // Generate chart data
    setChartData({
      labels: ['Day', 'Month', 'Year', 'Total', 'Birthday Number'],
      datasets: [{
        label: 'Number Values',
        data: [day, month, year, birthdaySum, finalNumber],
        backgroundColor: [
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 99, 132, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(255, 206, 86, 0.5)'
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)'
        ],
        borderWidth: 1
      }]
    });
  };

  const birthdayMeanings = {
    1: 'Leadership and independence',
    2: 'Harmony and cooperation',
    3: 'Creativity and self-expression',
    4: 'Stability and practicality',
    5: 'Freedom and adventure',
    6: 'Responsibility and nurturing',
    7: 'Spirituality and introspection',
    8: 'Ambition and power',
    9: 'Humanitarianism and compassion',
    11: 'Inspiration and intuition',
    22: 'Master builder and vision',
    33: 'Master teacher and compassion'
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Birthday Number Calculator | Discover Your Life Path</title>
        <meta name="description" content="Calculate your Birthday Number using our Numerology Calculator. Discover your life path and personal traits." />
        <meta name="keywords" content="birthday number, numerology calculator, life path, personal traits" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🎂</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Birthday Number Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Discover Your Life Path</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birthday">
            Birthday (YYYY-MM-DD):
          </label>
          <input
            type="date"
            id="birthday"
            name="birthday"
            value={birthday}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
          />
        </div>

        <button
          onClick={calculateBirthdayNumber}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Birthday Number
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {birthdayNumber && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Birthday Number</h3>
            <div className="text-center">
              <div className="text-6xl font-bold text-blue-600 mb-4">
                {birthdayNumber}
              </div>
              <p className="text-gray-700">
                This number reveals your life path and personal traits.
              </p>
            </div>
            {chartData && (
              <div className="mt-8 space-y-8">
                <div>
                  <h4 className="text-xl font-semibold mb-4">Calculation Breakdown</h4>
                  <div className="h-96">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: 'Birthday Number Calculation Steps'
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Example Calculation</h4>
                  <div className="space-y-4">
                    <p className="text-gray-700">
                      For the birthday "1990-05-15":
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Day:</p>
                        <p>15</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Month:</p>
                        <p>5</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Year:</p>
                        <p>1990</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Total:</p>
                        <p>2010</p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="font-medium">Result:</p>
                        <p>2 + 0 + 1 + 0 = 3</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Your Birthday Meaning</h4>
                  <p className="text-gray-700">
                    {birthdayMeanings[birthdayNumber]}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Birthday Number:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Birthday</h3>
              <p className="text-gray-700">
                Input your complete birthday in the format YYYY-MM-DD.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate" button to get your Birthday Number.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your Birthday Number and its meaning in the results section.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is a Birthday Number?</h3>
              <p className="text-gray-700">
                Your Birthday Number reveals your life path, personal traits, and how you approach life's challenges.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                It's calculated by adding the numbers in your birth date (day, month, and year), then reducing to a single digit or master number.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What's the difference between Birthday and other numerology numbers?</h3>
              <p className="text-gray-700">
                The Birthday Number represents your life path and personal traits, while other numbers like Expression and Soul Urge focus on different aspects of your being.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate Birthday Number calculation</li>
                <li>Visual breakdown of calculation steps</li>
                <li>Supports master numbers (11, 22, 33)</li>
                <li>Clean and intuitive interface</li>
                <li>Detailed explanation of results</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Discover your life path</li>
                <li>Understand your personal traits</li>
                <li>Learn how to navigate life's challenges</li>
                <li>Align with your life purpose</li>
                <li>Make decisions that align with your path</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirthdayNumberCalculator;